import { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import Kennzeichen, { KennzeichenArt } from './Kennzeichen';
import BeispieleKnopf from './BeispieleKnopf';
import Feldhinweis from './Feldhinweis';
import { FeldBefund } from './feldpruefungApi';

/**
 * Rahmen um ein Eingabefeld der Marketing-Oberfläche: Titel + Kennzeichen (Pflicht/Optional/…) + ein Satz
 * „Wofür / wo sieht es der Kunde“ + optional „Beispiele“ + Prüfhinweise darunter. Das Eingabefeld selbst
 * (TextField, Select, Editor) kommt als `children` — der Rahmen ändert kein Verhalten des Feldes.
 *
 * Plan 2026-10-22: Personas lasen jedes Feld als Pflicht, solange nichts anderes dranstand.
 */
const Feld: FC<{
  titel: string;
  art: KennzeichenArt;
  /** Ein Satz: wofür ist das Feld, wo sieht es der Kunde, was passiert, wenn es leer bleibt. */
  wofuer?: ReactNode;
  beispiele?: {
    feld: string;
    webinarId: string | null | undefined;
    onUebernehmen: (wert: string) => void;
    hinweis?: string;
  };
  befunde?: FeldBefund[];
  pruefungLaeuft?: boolean;
  /** Rechts im Kopf, z. B. ein Zusatzknopf. */
  aktion?: ReactNode;
  /** Ersetzt die Tooltip-Erklärung des Kennzeichens (Standard ist auf Webinare formuliert). */
  kennzeichenErklaerung?: string;
  children: ReactNode;
  sx?: object;
}> = ({
  titel,
  art,
  wofuer,
  beispiele,
  befunde,
  pruefungLaeuft,
  aktion,
  kennzeichenErklaerung,
  children,
  sx,
}) => (
  <Box sx={{ mb: 2, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {titel}
      </Typography>
      <Kennzeichen art={art} erklaerung={kennzeichenErklaerung} />
      <Box sx={{ flex: 1 }} />
      {beispiele && (
        <BeispieleKnopf
          feld={beispiele.feld}
          webinarId={beispiele.webinarId}
          onUebernehmen={beispiele.onUebernehmen}
          hinweis={beispiele.hinweis}
        />
      )}
      {aktion}
    </Box>
    {wofuer && (
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ mb: 0.75, maxWidth: '78ch' }}
      >
        {wofuer}
      </Typography>
    )}
    {children}
    {(befunde?.length || pruefungLaeuft) && (
      <Feldhinweis befunde={befunde ?? []} laeuft={pruefungLaeuft} />
    )}
  </Box>
);

export default Feld;
